/** Кирпичики интерфейса, общие для витрины и админки.
 *
 * Здесь живёт дизайн-система в виде компонентов: если кнопка выглядит одинаково
 * в двух местах, это должно быть одно и то же, а не два похожих набора классов.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { tap } from './tg';

type ButtonTone = 'accent' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  /** Мелкая кнопка в ряду — не главное действие экрана */
  small?: boolean;
}

const TONES: Record<ButtonTone, string> = {
  accent:
    'bg-linear-135 from-mint to-mint-deep text-mint-ink accent-glow ' +
    'active:scale-[.97] active:shadow-none',
  ghost:
    'glass text-frost active:scale-[.97] active:border-mint',
  danger:
    'bg-danger text-white shadow-[0_6px_18px_rgba(255,90,106,.22)] active:scale-[.97]',
};

export function Button({
  tone = 'accent',
  small = false,
  className = '',
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      onClick={(event) => {
        if (!rest.disabled) tap();
        onClick?.(event);
      }}
      className={[
        'rounded-xl font-[650] tracking-[-.01em] transition-[transform,opacity,box-shadow]',
        'duration-150 ease-glass disabled:opacity-50 disabled:shadow-none',
        small ? 'px-3 py-2 text-[13px]' : 'px-4 py-3 text-sm',
        TONES[tone],
        className,
      ].join(' ')}
    />
  );
}

/** Карточка-стекло: основной материал мира. */
export function Card({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`glass wet-edge rounded-card ${className}`}>{children}</div>
  );
}

/** Пояснение под заголовком: тише текста, но не шёпотом. */
export function Note({
  children,
  tone = 'hint',
}: {
  children: ReactNode;
  tone?: 'hint' | 'warn';
}) {
  return (
    <div
      className={`my-2 text-[13px] ${tone === 'warn' ? 'text-danger' : 'text-hint'}`}
    >
      {children}
    </div>
  );
}

export function Skeleton({ className = 'h-28' }: { className?: string }) {
  return (
    <div
      className={`skeleton rounded-card ${className}`}
      aria-hidden
    />
  );
}

/** Всплывающая строка внизу: сообщает об исходе действия и уходит сама. */
export function useToast() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 2200);
    return () => clearTimeout(timer);
  }, [message]);

  const toast = (
    <div
      className={[
        'fixed inset-x-3 bottom-[var(--above-tabs)] z-50',
        'mx-auto max-w-[420px] rounded-xl px-4 py-3 text-center text-sm',
        'bg-night-2/95 text-frost shadow-[0_10px_30px_rgba(0,0,0,.4)]',
        // Только появление, и ничего больше: `transition-all` заставлял
        // тост ещё и переезжать по вертикали, когда экран товара сообщал
        // высоту своей панели — вместо мгновенного места он полз снизу
        'backdrop-blur-md transition-[opacity,transform] duration-200 ease-glass',
        message ? 'opacity-100 translate-y-0' : 'pointer-events-none opacity-0 translate-y-2',
      ].join(' ')}
      role="status"
    >
      {message}
    </div>
  );

  return { toast, show: setMessage };
}
